<script lang="ts">
    import route from '@/Util/routes';
    import { getRequest } from '@/Util/utils';
    import { _ } from 'svelte-i18n';
    import { createEventDispatcher } from 'svelte';
    import XfaiGeneralSettings from '@/Program/ProgramElements/XfaiGeneralSettings.svelte';
    import { WeeklyHoursSlider } from '@/Program/ProgramElements/SetupFormInputs';
    import { defaultTrainingPlanSettings, type TrainingPlanSettings } from '@/Common/constants';


    export let cal_settings;
    export let availableStats;
    export let displayedStats;
    export let myFitness;
    export let username;

    let { userParams, trainingPlan, trainingAdvice, trainingAdviceUpdatedAt } = myFitness;
    $: isPremium = $userParams.premium;
    $: isTrial = $userParams.trial;
    //@ts-ignore
    const jq = window.$;
    //@ts-ignore
    const Xert = window.Xert;

    // Program Settings of the current profile's training plan, same as Profile Settings > General.
    // Edited on a copy so closing the modal without saving discards the changes.
    let programSettings: TrainingPlanSettings = { ...defaultTrainingPlanSettings };
    let initialProgramSettings = '';

    let _displayedStats = {
        'total_time': false,
        'total_distance': false,
        'xss': false,
        'ramp_rate': false,
        'xss_focus': false,
        'suggested_focus': false,
        'polarity_ratio': false,
        'tl': false,
        'ftp': false,
        'hie': false,
        'pp': false,
        'ltp': false,
        'athlete_type': false,
        'xss_spr': false,
        'ltl': false,
        'htl': false,
        'ptl': false,
        'xss_week': false,
        'xlss': false,
        'xhss': false,
        'xpss': false,
        'rl': false,
        'form': false
    };
    let settingsForm: HTMLFormElement;
    let hideOtherProfiles = true;
    let useAggregateCard = true;
    let autoScheduleWorkout = false;
    let startOfWeek = 0;

    export async function initCalendarModal(event) {
        if (availableStats && displayedStats) {
            availableStats.forEach(availableStat => {
                _displayedStats[availableStat.value] = (displayedStats.find(displayedStat=>displayedStat.value === availableStat.value) !== undefined);
            });
        }
        
        if (cal_settings) {
            hideOtherProfiles = cal_settings.hide_other_profiles;
            useAggregateCard = cal_settings.use_aggregate_card;
            autoScheduleWorkout = cal_settings.autogen_workouts;
            startOfWeek = cal_settings.start_of_week?.toString();
        }

        programSettings = { ...defaultTrainingPlanSettings, ...($trainingPlan?.settings ?? {}) };
        initialProgramSettings = JSON.stringify(programSettings);

        // The help popovers in Program Settings open outside the modal. Bootstrap's focus trap would pull focus back into
        // the modal each time, which the popover then takes again, until the call stack overflows.
        jq('#settings-modal').one('shown.bs.modal', () => jq(document).off('focusin.bs.modal'));
        jq('#settings-modal').modal('show');
    }

    // Same request as Profile Settings > General. Only the settings are sent, so availability is left alone.
    async function saveProgramSettings() {
        const data = await window.fetch(route('UserController@postTrainingProgram'), {
            ...getRequest(),
            method: 'POST',
            body: JSON.stringify({ settings: programSettings, forUser: username })
        }).then(response => response.json()).catch(() => ({ result: false }));

        if (data.result == false) {
            Xert.warning("Failed to update program settings.");
            return;
        }
        if (data.training_plan) {
            trainingPlan.set(data.training_plan);
        }
        if (data.training_advice) {
            if (data.training_advice.timestamp)
                trainingAdviceUpdatedAt.set(new Date(data.training_advice.timestamp));
            trainingAdvice.set(data.training_advice);
        }
        Xert.global("Settings updated");
    }

    const dispatch = createEventDispatcher();
    async function submitSettings() {
        jq('#settings-modal').modal('hide');

        

        let newDisplayedStats = [];
        for (const [value, checked] of Object.entries(_displayedStats)) {
            if (checked) {
                let label = availableStats.find(availableStat => availableStat.value === value).label;
                newDisplayedStats.push({'value': value, 'label': label, 'preselected': true});
            }
        }
        displayedStats = newDisplayedStats;

        const res = new FormData(settingsForm);
        res.append('hide_other_profiles', hideOtherProfiles ? 'true' : 'false');
        res.append('use_aggregate_card', useAggregateCard ? 'true' : 'false');
        res.append('autogen_workouts', autoScheduleWorkout ? 'true' : 'false');
        res.append('forUser', username);
        await window.fetch(route('CalendarController@saveCalendarSettings'), {
            ...getRequest(),
            method: 'POST',
            body: JSON.stringify(Object.fromEntries(res))
        });
        
        if (JSON.stringify(programSettings) !== initialProgramSettings) {
            await saveProgramSettings();
        }

        if (cal_settings) {
            cal_settings.hide_other_profiles = hideOtherProfiles;
            cal_settings.use_aggregate_card = useAggregateCard;
            cal_settings.autogen_workouts = autoScheduleWorkout;
            cal_settings.start_of_week = Number(startOfWeek);
        }
        dispatch('submitSettings', null);
    }
</script>

<div class="modal fade" id="settings-modal" tabindex="-1" role="dialog" aria-labelledby="myModalLabel">
    <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content flex flex-col h-full">
            <div class="modal-header">
                <div class="modal-title">Calendar Settings</div>
                <button type="button" class="modal-close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
            </div>
            <div class="modal-body overflow-y-auto flex-1 pt-0">
                <form bind:this={settingsForm}>
                    <div class="row">
                        <div class="settings-container">
                            <div class="settings-dropdown">
                                <div>Start of the Week</div>
                                <select name="start_of_week" bind:value={startOfWeek}>
                                    <option value="0">Sunday</option>
                                    <option value="1">Monday</option>
                                </select>
                            </div>
                            <div class="settings-toggle">
                                <div class="toggle-wrapper">
                                    <input type="checkbox" id="hide-other-profiles" bind:checked={hideOtherProfiles}/><label for="hide-other-profiles"/>
                                    <small>Hide Other Profiles</small>
                                </div>
                            </div>
                            <div class="settings-toggle">
                                <div class="toggle-wrapper">
                                    <input type="checkbox" id="use-aggregate-card" bind:checked={useAggregateCard}/><label for="use-aggregate-card"/>
                                    <small>Summarize days with multiple activities</small>
                                </div>
                            </div>
                            <div class="settings-toggle">
                                <div class="toggle-wrapper">
                                    <input type="checkbox" id="auto-schedule-workout" bind:checked={autoScheduleWorkout}/><label for="auto-schedule-workout"/>
                                    <small>Automatically schedule a recommended workout of the day</small>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div>Weekly Stats</div>
                        <div class="stats-container">
                            {#each availableStats as stat, i}
                                <input type="checkbox" id="stat-{i}" name={stat['title']} value={stat['value']} bind:checked={_displayedStats[stat['value']]}>
                                <label for="stat-{i}">{stat['label']}</label>
                            {/each}
                        </div>
                    </div>
                </form>
                {#if $trainingPlan}
                    <div class="row">
                        <XfaiGeneralSettings bind:settings={programSettings} calculatedDifficulty={$trainingAdvice?.difficulty} alwaysOpen={true} showDefaultsButton={false} compact={true}>
                            <span>Max Weekly Hours</span>
                            <WeeklyHoursSlider bind:hours={programSettings.maxWeeklyHours} />
                        </XfaiGeneralSettings>
                    </div>
                {/if}
            </div>
            <div class="modal-footer">
                <button value="save" type="submit" class="btn btn-default pull-right" data-toggle="tooltip" title="Save" id="submit-settings" on:click={submitSettings}>Save</button>
            </div>
        </div>
    </div>
</div>

<style lang="scss">
    .row {
        margin: 16px 0px;
    }
    .modal-body {
        color: #3d3d3d;
        line-height: 24px;
        letter-spacing: 0.11px;
    }
    .settings-container {
        display: flex;
        gap: 15px 48px;
        align-items: center;
        flex-wrap: wrap;
    }
    .settings-dropdown {
        select {
            margin-top: 3px;
            border: 1px solid #979797;
            border-radius: 3px;
            background: white;
            height: 30px;
            width: 124px;
        }
    }
    .stats-container {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        align-items: center;
        margin-top: 3px;
        input {
            display: none;
        }
        label {
            &:hover {
                cursor: pointer;
            }
            margin: 0px;
            padding: 5px 8px;
            background: #2F707633;
            border-radius: 3px;
            color: #2F7076;
        }
        input:checked + label {
            background: #2F7076;
            color: white;
        }
    }
    .toggle-wrapper small {
        font-size: 14px;
        padding-left: 10px;;
    }
    .btn.btn-default {
        padding: 8px 30px;
        border-radius: 4px;
        font-family: 'Roboto';
        font-weight: 400;
        line-height: 19px;
        text-align: center;
        letter-spacing: 0.48px;
        float: none;
        &[type="submit"] {
            background: #2F7076;
            color: white;
            &:hover {
                background: #204d51;
            }
        }
    }
</style>
